import React from 'react';
import { Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import useWalletDisplayMode from '@/hooks/hypertracker/useWalletDisplayMode';
import useIsMemberAuthorized from '@/hooks/useIsMemberAuthorized';
import { WALLET_DISPLAY_MODE } from '@/state/clientStorage/clientStorageTypes';
import { setModalWindow } from '@/state/hypertracker/hypertackerSlice';
import { MODAL_TYPE_HANDLER } from '@/lib/types/hypertrackerTypes';
import { AUTH_TYPE } from '@/lib/types/generalTypes';
import { SwitcherButton, SwitcherGroup } from './WalletDisplaySwitcherStyled';

interface WalletDisplaySwitcherProps {
    onLoginPrompt?: () => void;
}

const WalletDisplaySwitcher: React.FC<WalletDisplaySwitcherProps> = ({ onLoginPrompt }) => {
    const { t } = useTranslation('translation', { keyPrefix: 'profile_menu_drawer' });
    const dispatch = useDispatch();
    const isMemberAuthorized = useIsMemberAuthorized();
    const { walletDisplayMode, changeWalletDisplayMode } = useWalletDisplayMode();

    const handleChange = (_event: React.MouseEvent<HTMLElement>, mode: WALLET_DISPLAY_MODE) => {
        if (!isMemberAuthorized) {
            dispatch(
                setModalWindow({
                    modalType: MODAL_TYPE_HANDLER.IS_AUTH_MODAL,
                    authorizationModalType: AUTH_TYPE.SIGN_IN,
                }),
            );
            onLoginPrompt?.();
            return;
        }

        if (!mode) return;
        changeWalletDisplayMode(mode);
    };

    return (
        <Stack gap={'8px'} data-testid="hypertracker-wallet-display-switcher">
            <Typography variant="p12" color="common.white" component={'p'}>
                {t('wallet_display')}
            </Typography>
            <SwitcherGroup exclusive value={walletDisplayMode} onChange={handleChange}>
                <SwitcherButton
                    value={WALLET_DISPLAY_MODE.NAMES}
                    data-testid="hypertracker-wallet-display-switcher-names"
                >
                    {t('wallet_display_names')}
                </SwitcherButton>
                <SwitcherButton
                    value={WALLET_DISPLAY_MODE.ADDRESSES}
                    data-testid="hypertracker-wallet-display-switcher-addresses"
                >
                    {t('wallet_display_addresses')}
                </SwitcherButton>
            </SwitcherGroup>
            <Typography
                component={'span'}
                fontSize={'12px'}
                color={'text.secondary'}
                fontWeight={400}
                lineHeight={'16px'}
            >
                {t('wallet_display_description')}
            </Typography>
        </Stack>
    );
};

export default WalletDisplaySwitcher;
